import React, {
  ReactNode,
  SyntheticEvent,
  useContext,
  useCallback,
  useState,
  useEffect,
} from "react";
import { Autocomplete, Grid } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { debounce } from "@mui/material/utils";
import { ToastContext, TOAST_TYPE } from "context/toastContext";
import { MakeModelAutocompleteOption } from "types";
import { makeModelAutocomplete } from "api/vehicles";
import { TextValidator } from "react-material-ui-form-validator";
import AuturaTextField from "../inputs/AuturaTextField";

import { useTranslation } from "react-i18next";

export interface MakeModelAutocompleteProps {
  make: string | null;
  model: string | null;
  setMake: (make: string | null) => void;
  setModel: (model: string | null) => void;
  readonly?: boolean;
  required?: boolean;
  variant?: "standard" | "outlined" | "filled";
  margin?: "none" | "dense" | "normal";
  disabled?: boolean;
}

type ForceType = "make" | "model" | null;

const MakeModelAutocomplete = ({
  make,
  model,
  setMake,
  setModel,
  variant = "outlined",
  margin = "none",
  readonly = false,
  disabled = false,
  required = false,
}: MakeModelAutocompleteProps): ReactNode => {
  const displayToast = useContext(ToastContext);
  const { t } = useTranslation();
  const [makeOpen, setMakeOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [makeOptions, setMakeOptions] = useState<Array<string>>([]);
  const [modelOptions, setModelOptions] = useState<Array<string>>([]);
  const [coupledOptions, setCoupledOptions] = useState<
    Array<MakeModelAutocompleteOption>
  >([]);
  const [makeInputValue, setMakeInputValue] = useState("");
  const [modelInputValue, setModelInputValue] = useState("");
  const makeLoading =
    makeOpen && makeOptions.length === 0 && makeInputValue !== "";
  const modelLoading =
    modelOpen && modelOptions.length === 0 && modelInputValue !== "";

  useEffect(() => {
    if (makeInputValue === "" && modelInputValue === "") {
      return;
    }
    debounce(async () => {
      fetchSuggesitons(null);
    }, 300)();
  }, [makeInputValue, modelInputValue]);

  useEffect(() => {
    // they can only be equal if they are both false
    if (makeOpen === modelOpen) {
      setCoupledOptions([]);
      setMakeOptions([]);
      setModelOptions([]);
    }
  }, [makeOpen, modelOpen]);

  const fetchSuggesitons = useCallback(
    async (force: ForceType) => {
      if (makeInputValue === "" && modelInputValue === "") {
        return;
      }
      try {
        const res = await makeModelAutocomplete(
          makeInputValue,
          modelInputValue,
        );
        if (res.ok) {
          const data = await res.json();
          setCoupledOptions(data);
          if (makeOpen || force === "make") {
            const makesArr: string[] = data.map(
              (option: MakeModelAutocompleteOption) => option.make,
            );
            const makesSet = new Set(makesArr);
            setMakeOptions(Array.from(makesSet));
          }
          if (modelOpen || force === "model") {
            const modelOptions = data.map(
              (option: MakeModelAutocompleteOption) => option.model,
            );
            setModelOptions(modelOptions);
          }
        } else {
          throw new Error(`${res.status} ${res.statusText}`);
        }
      } catch (e) {
        const error = e as Error;
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: error.message,
        });
      }
    },
    [makeOpen, modelOpen, makeInputValue, modelInputValue],
  );

  const handleOnMakeChange = (e: SyntheticEvent, v: string | null) => {
    // this is a clear event
    if (v === null) {
      setModelInputValue("");
      setMakeInputValue("");
      setMake(null);
      setModel(null);
      return;
    }
    setMakeInputValue(v || "");
    setMake(v || null);
  };
  const handleOnModelChange = (e: SyntheticEvent, v: string | null) => {
    // this is a clear event
    if (v === null) {
      setModelInputValue("");
      setModel(null);
      return;
    }

    setModel(v || null);
    setModelInputValue(v || "");
    const option = coupledOptions.find(
      (option: MakeModelAutocompleteOption) => option.model === model,
    );
    if (option) {
      setMake(option.make);
    }
  };
  const handleOnMakeInputChange = (e: SyntheticEvent, value: string) => {
    setMakeInputValue(value);
  };
  const handleOnModelInputChange = (e: SyntheticEvent, value: string) => {
    setModelInputValue(value);
  };
  const getOptionLabel = (option: string | MakeModelAutocompleteOption) => {
    if (typeof option === "string") {
      return option;
    }
    return "";
  };

  if (readonly) {
    return (
      <Grid container width={"100%"} spacing={1.5}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <AuturaTextField
            value={make || ""}
            name={"vehicle.make"}
            label={t("vehicle.make", { ns: "entities" })}
            margin={margin}
            variant={variant}
            readonly
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <AuturaTextField
            value={model || ""}
            name={"vehicle.model"}
            label={t("vehicle.model", { ns: "entities" })}
            margin={margin}
            variant={variant}
            readonly
          />
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid container width={"100%"} spacing={1.5}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Autocomplete
            disabled={disabled}
            id="make-autocomplete"
            blurOnSelect
            open={makeOpen}
            onOpen={() => {
              setMakeOpen(true);
              fetchSuggesitons("make");
            }}
            onClose={() => {
              setMakeOpen(false);
              setMakeOptions([]);
            }}
            filterSelectedOptions
            filterOptions={(x) => x}
            options={makeOptions}
            value={make}
            fullWidth
            includeInputInList
            noOptionsText={!make && t("enterTextToSearch")}
            getOptionLabel={getOptionLabel}
            loading={makeLoading}
            loadingText={t("searching")}
            onInputChange={handleOnMakeInputChange}
            onChange={handleOnMakeChange}
            renderInput={(params) => (
              <TextValidator
                {...params}
                label={`${t("vehicle.make")} ${required ? " *" : ""}`}
                margin={margin}
                variant={variant}
                value={make}
                name={"vehicle.make"}
                validators={required ? ["required"] : []}
                errorMessages={
                  required ? [t("vehicle.make") + " is required"] : []
                }
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {makeLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Autocomplete
            id="model-autocomplete"
            blurOnSelect
            disabled={disabled}
            open={modelOpen}
            onOpen={() => {
              setModelOptions([]);
              setModelOpen(true);
              fetchSuggesitons("model");
            }}
            onClose={() => {
              setModelOpen(false);
              setModelOptions([]);
            }}
            filterSelectedOptions
            filterOptions={(x) => x}
            options={modelOptions}
            getOptionLabel={getOptionLabel}
            value={model}
            fullWidth
            noOptionsText={t("enterTextToSearch")}
            loading={modelLoading}
            loadingText={t("searching")}
            onInputChange={handleOnModelInputChange}
            onChange={handleOnModelChange}
            renderInput={(params) => (
              <TextValidator
                {...params}
                value={model}
                name={"vehicle.model"}
                label={
                  required
                    ? t("vehicle.model", { ns: "entities" }) + " *"
                    : t("vehicle.model", { ns: "entities" })
                }
                margin={margin}
                variant={variant}
                validators={required ? ["required"] : []}
                errorMessages={[
                  t("vehicle.model", { ns: "entities" }) + " is required",
                ]}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {modelLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    );
  }
};

export default MakeModelAutocomplete;
